import { useMemo } from 'react';
import { useSelector } from 'umi';
import { gameInfoType } from '@op/types/common';
import usePageName from './usePageName';

/** 游戏平台类型 */
export type GameType = 'pc' | 'mobile' | 'console';

interface UseHasMenuOptions {
  /** 父级菜单名称（英文），传入后会在指定父级下查找 */
  parentPageName?: string;
  /** 使用 id 作为查找键，默认使用 name_en */
  useId?: boolean;
  /**
   * 限制游戏平台类型，只有在指定类型的游戏中才会判断菜单是否存在
   * 例如：gameType: 'pc' 表示只在 PC 游戏中判断，其他类型游戏直接返回 true（不限制）
   */
  gameType?: GameType | GameType[];
}

/**
 * 检查当前游戏类型是否匹配指定的类型
 */
function checkGameType(
  gameInfo: gameInfoType | undefined,
  gameType: GameType | GameType[] | undefined,
): boolean {
  if (!gameType) return true;
  if (!gameInfo?.entity_type) return false;

  const types = Array.isArray(gameType) ? gameType : [gameType];
  return types.includes(gameInfo.entity_type as GameType);
}

/**
 * 判断菜单是否存在的 Hook
 * 基于 usePageName，自动从 opinionCommon store 获取 listMenu 和 gameInfo
 *
 * @example
 * // 基本用法 - 判断单个菜单是否存在
 * const { hasMenu: hasSteamMenu } = useHasMenu('Steam Performance');
 *
 * @example
 * // 只在 PC 游戏中判断菜单，其他类型游戏不限制（返回 true）
 * const { hasMenu } = useHasMenu('Steam Performance', { gameType: 'pc' });
 *
 * @example
 * // 只在 PC 或 Console 游戏中判断菜单，Mobile 游戏不限制（返回 true）
 * const { hasMenu } = useHasMenu('Menu Name', { gameType: ['pc', 'console'] });
 *
 * @example
 * // 在指定父级下查找菜单
 * const { hasMenu } = useHasMenu('Menu Name', { parentPageName: 'Parent Menu' });
 *
 * @example
 * // 使用 menuId 查找
 * const { hasMenu } = useHasMenu('menu-123', { useId: true });
 */
export default function useHasMenu(
  name: string,
  options: UseHasMenuOptions = {},
) {
  const { parentPageName, useId = false, gameType } = options;

  const { listMenu, listMenuLoading, gameInfo } = useSelector(
    ({ opinionCommon }: any) => ({
      listMenu: opinionCommon.listMenu,
      listMenuLoading: opinionCommon.listMenuLoading,
      gameInfo: opinionCommon.gameInfo as gameInfoType,
    }),
  );

  // 游戏类型是否匹配
  const isGameTypeMatch = useMemo(
    () => checkGameType(gameInfo, gameType),
    [gameInfo, gameType],
  );

  // 基于 usePageName 获取菜单信息
  const { hasMenu: menuExists, findLoading } = usePageName({
    name: useId ? undefined : decodeURIComponent(name),
    menuId: useId ? name : undefined,
    listMenu,
    parentPageName,
  });

  // 最终 hasMenu：游戏类型不匹配时返回 true（不限制）
  const hasMenu = useMemo(() => {
    if (!isGameTypeMatch) return true;
    return menuExists;
  }, [isGameTypeMatch, menuExists]);

  return {
    /** 菜单是否存在（受 gameType 限制影响） */
    hasMenu,
    /** 菜单列表是否正在加载 */
    loading: listMenuLoading || findLoading,
    /** 当前游戏信息 */
    gameInfo,
    /** 当前游戏类型是否匹配 */
    isGameTypeMatch,
  };
}
